// TODO: Break this module up into smaller modules
import { isDocumentFragment, isElement, isHTMLElement, isNonNullableObject, isObserver, isSVGElement, isText, isUnknownDictionary, normalizeSubscriber, } from "@knyt/artisan";
import { ElementBuilderDomHTMLSymbol, ElementBuilderDomSVGSymbol, ElementBuilderIsElementSymbol, ElementBuilderKindSymbol, ElementBuilderMarkupHTMLSymbol, ElementBuilderMarkupSVGSymbol, ElementBuilderTargetSymbol, ElementDeclarationAttachmentSymbol, ElementDeclarationKind, ElementDeclarationSymbol, FragmentTypeSymbol, KEY_ATTRIBUTE, KEY_DATASET_PROPERTY, ViewBuilderIsViewSymbol, ViewBuilderTargetSymbol, ViewDeclarationSymbol, } from "../constants.js";
import { isStringDocumentFragment } from "./StringDocumentFragment.js";
/**
 * A type guard that determines if a value is a ref handler.
 *
 * @internal scope: workspace
 */
/*
 * ### Private Remarks
 *
 * This type guard is used internally by the renderer to determine if a value is a ref mutator.
 * The implementation is very simple, so we're making an assumption that the given function is a ref mutator.
 * The main purpose of this type guard is to provide type safety when using ref mutators.
 */
export function isElementDeclarationRe(value) {
    // We assume that any function is a next handler
    return typeof value == "function" || isObserver(value);
}
/**
 * Determines if a value is valid input for an element declaration ref.
 */
export function isElementDeclarationRefInput(value) {
    return value === undefined || isElementDeclarationRe(value);
}
/**
 * Determines if a value is a valid key for an element declaration.
 */
export function isElementDeclarationKey(value) {
    return typeof value === "string";
}
/**
 * Determines if a value is valid input for an element declaration key.
 */
export function isElementDeclarationKeyInput(value) {
    return value === undefined || isElementDeclarationKey(value);
}
/**
 * Determines if a value are valid props for an element declaration.
 */
export function isElementDeclarationProps(value) {
    return isUnknownDictionary(value);
}
/**
 * Determines if a value are valid attributes for an element declaration.
 */
export function isElementDeclarationAttributes(value) {
    return isUnknownDictionary(value);
}
/**
 * Determines if a value is valid input for an element declaration attributes.
 */
export function isElementDeclarationAttributesInput(value) {
    return value === undefined || isElementDeclarationAttributes(value);
}
export function isElementDeclarationPropsInput(value) {
    return value === undefined || isElementDeclarationProps(value);
}
/**
 * Normalizes an element declaration ref to a ref handler.
 *
 * @internal scope: workspace
 */
export function normalizeElementDeclarationRef(ref) {
    return normalizeSubscriber(ref);
}
export function isElementBuilder(value) {
    return (typeof value == "object" &&
        value !== null &&
        value[ElementBuilderIsElementSymbol] === true);
}
export function assertElementBuilder(value) {
    if (!isElementBuilder(value)) {
        throw new TypeError("Invalid element builder");
    }
}
export function isViewBuilder(value) {
    return (typeof value == "object" &&
        value !== null &&
        value[ViewBuilderIsViewSymbol] === true);
}
export function assertViewBuilder(value) {
    if (!isViewBuilder(value)) {
        throw new TypeError("Invalid view builder");
    }
}
(function (isElementBuilder) {
    function MarkupHTML(builder) {
        return (builder[ElementBuilderKindSymbol] === ElementBuilderMarkupHTMLSymbol);
    }
    isElementBuilder.MarkupHTML = MarkupHTML;
    function MarkupSVG(builder) {
        return (builder[ElementBuilderKindSymbol] === ElementBuilderMarkupSVGSymbol);
    }
    isElementBuilder.MarkupSVG = MarkupSVG;
    function Markup(builder) {
        return MarkupHTML(builder) || MarkupSVG(builder);
    }
    isElementBuilder.Markup = Markup;
    function DomHTML(builder) {
        return (builder[ElementBuilderKindSymbol] === ElementBuilderDomHTMLSymbol);
    }
    /** @deprecated SVG is only supported in the `Markup` builder. */
    function DomSVG(builder) {
        return (builder[ElementBuilderKindSymbol] === ElementBuilderDomSVGSymbol);
    }
    // TODO: Rename for clarity
    function DOM(builder) {
        return DomHTML(builder) || DomSVG(builder);
    }
    isElementBuilder.DOM = DOM;
    function Valid(input) {
        return Markup(input) || DOM(input);
    }
    isElementBuilder.Valid = Valid;
    function assertValid(input) {
        if (!Valid(input)) {
            throw new TypeError("Unknown element builder kind");
        }
    }
    isElementBuilder.assertValid = assertValid;
    function Fragment(builder) {
        return isElementDeclaration.Fragment(getElementDeclarationFromElementBuilder(builder));
    }
    isElementBuilder.Fragment = Fragment;
})(isElementBuilder || (isElementBuilder = {}));
/**
 * Attaches an element declaration to a singular element.
 */
/*
 * ### Private Remarks
 *
 * Element declarations are not attached to fragments,
 * because fragments are transient and treated as a collection of nodes.
 * Element declarations are only attached to singular elements that
 * will persist in the DOM.
 */
export function attachElementDeclaration(element, declaration) {
    element[ElementDeclarationAttachmentSymbol] = declaration;
    return element;
}
/**
 * Determines if the element has an element declaration attached to it.
 *
 * @public
 * @alpha
 */
// TODO: Rename to `elementHasElementDeclaration`?
export function hasElementDeclaration(el) {
    return ElementDeclarationAttachmentSymbol in el;
}
/**
 * Determines if the element can have an element declaration attached to it.
 *
 * @public
 * @alpha
 */
/*
 * ### Private Remarks
 *
 * This is an alias of `isSingularElement` for clarity of intent and future-proofing
 */
// TODO: Rename to `canElementHaveElementDeclaration`?
export const canHaveDeclaration = isSingularElement;
/**
 * Gets the element declaration attached to a given value.
 *
 * @public
 * @alpha
 */
// TODO: Rename to `getElementDeclarationFromElement`?
export function getElementDeclaration(value) {
    return canHaveDeclaration(value) && hasElementDeclaration(value)
        ? value[ElementDeclarationAttachmentSymbol]
        : undefined;
}
/**
 * Gets the key of an element declaration from the key attribute.
 *
 * @internal scope: package
 */
function getElementDeclarationKeyFromAttribute(value) {
    if (isHTMLElement(value)) {
        return value.dataset[KEY_DATASET_PROPERTY];
    }
    if (isElement(value)) {
        return value.getAttribute(KEY_ATTRIBUTE) ?? undefined;
    }
    return undefined;
}
/**
 * Gets the key of an element declaration from a given node.
 *
 * If the node is an element, it will first check if the element has an
 * attached element declaration. If not, it will check for the key attribute.
 *
 * @public
 */
// TODO: Rename for clarity, e.g. `getKeyFromNode`?
export function getElementDeclarationKey(value) {
    return (getElementDeclaration(value)?.key ??
        getElementDeclarationKeyFromAttribute(value));
}
/**
 * Gets the mutable element declaration from an element builder.
 *
 * @internal scope: workspace
 */
// TODO: Rename for clarity
export function getMutableElementDeclarationFromElementBuilder(builder) {
    return builder[ElementBuilderTargetSymbol];
}
/**
 * Gets the mutable view declaration from an view builder.
 *
 * @internal scope: workspace
 */
export function getMutableViewDeclarationFromViewBuilder(builder) {
    return builder[ViewBuilderTargetSymbol];
}
/**
 * Gets the readonly view declaration from an view builder.
 *
 * @internal scope: workspace
 */
export function getViewDeclaration(builder) {
    return getMutableViewDeclarationFromViewBuilder(builder);
}
/**
 * Gets the element declaration from a singular element.
 *
 * @public
 * @alpha
 */
/*
 * ### Private Remarks
 *
 * This function returns a readonly version of the element declaration,
 * to prevent mutation of the element declaration.
 * We want to discourage mutation of the element declaration,
 * outside of the element builder API, to prevent unexpected behavior.
 */
export function getElementDeclarationFromElementBuilder(builder) {
    return getMutableElementDeclarationFromElementBuilder(builder);
}
/**
 * Extracts the element type from an element builder.
 */
export function getElementBuilderType(builder) {
    return getElementDeclarationFromElementBuilder(builder).type;
}
/**
 * Extracts the element type from an element builder.
 */
export function extractTagNameFromElementBuilder(builder) {
    const elementType = getElementBuilderType(builder);
    return elementType === FragmentTypeSymbol ? undefined : elementType;
}
export function isElementBuilderChildBasic(value) {
    return (isElement(value) ||
        isDocumentFragment(value) ||
        isStringDocumentFragment(value) ||
        typeof value === "string" ||
        typeof value === "number" ||
        value == null ||
        value === false);
}
export function isElementDeclaration(value) {
    return (isNonNullableObject(value) &&
        ElementDeclarationSymbol in value &&
        value[ElementDeclarationSymbol] === true);
}
export function isViewDeclaration(value) {
    return (isNonNullableObject(value) &&
        ViewDeclarationSymbol in value &&
        value[ViewDeclarationSymbol] === true);
}
export function isElementDeclarationKindDom(kind) {
    return (kind === ElementDeclarationKind.DomHTML ||
        kind === ElementDeclarationKind.DomSVG);
}
export function isElementDeclarationKindMarkup(kind) {
    return (kind === ElementDeclarationKind.MarkupHTML ||
        kind === ElementDeclarationKind.MarkupSVG);
}
(function (isElementDeclaration) {
    function DomHTML(declaration) {
        return (isElementDeclaration(declaration) &&
            declaration.kind === ElementDeclarationKind.DomHTML);
    }
    isElementDeclaration.DomHTML = DomHTML;
    function DomSVG(declaration) {
        return (isElementDeclaration(declaration) &&
            declaration.kind === ElementDeclarationKind.DomSVG);
    }
    isElementDeclaration.DomSVG = DomSVG;
    function DOM(element) {
        return DomHTML(element) || DomSVG(element);
    }
    isElementDeclaration.DOM = DOM;
    function MarkupHTML(declaration) {
        return (isElementDeclaration(declaration) &&
            declaration.kind === ElementDeclarationKind.MarkupHTML);
    }
    isElementDeclaration.MarkupHTML = MarkupHTML;
    function MarkupSVG(declaration) {
        return (isElementDeclaration(declaration) &&
            declaration.kind === ElementDeclarationKind.MarkupSVG);
    }
    isElementDeclaration.MarkupSVG = MarkupSVG;
    function Markup(element) {
        return MarkupHTML(element) || MarkupSVG(element);
    }
    isElementDeclaration.Markup = Markup;
    function Fragment(declaration) {
        return (isElementDeclaration(declaration) &&
            declaration.type === FragmentTypeSymbol);
    }
    isElementDeclaration.Fragment = Fragment;
    function Singular(declaration) {
        return isElementDeclaration(declaration) && !Fragment(declaration);
    }
    isElementDeclaration.Singular = Singular;
})(isElementDeclaration || (isElementDeclaration = {}));
export function cloneElementDeclaration(declaration) {
    return {
        [ElementDeclarationSymbol]: true,
        kind: declaration.kind,
        type: declaration.type,
        props: { ...declaration.props },
        children: [...declaration.children],
        ref: declaration.ref,
        key: declaration.key,
        listeners: { ...declaration.listeners },
        renderMode: declaration.renderMode,
        attributes: { ...declaration.attributes },
    };
}
export function createEmptyElementDeclaration(type, kind) {
    return {
        [ElementDeclarationSymbol]: true,
        type,
        kind,
        props: {},
        children: [],
        ref: undefined,
        key: undefined,
        listeners: undefined,
        renderMode: undefined,
        attributes: undefined,
    };
}
export function createEmptyView(render) {
    return {
        [ViewDeclarationSymbol]: true,
        props: {},
        children: [],
        ref: undefined,
        key: undefined,
        render,
    };
}
export function cloneView(view) {
    return {
        [ViewDeclarationSymbol]: true,
        props: { ...view.props },
        children: [...view.children],
        ref: view.ref,
        key: view.key,
        render: view.render,
    };
}
export function nodesToElementBuilderChildren(childNodes) {
    return Array.from(childNodes)
        .map((childNode) => (isText(childNode) ? childNode.textContent : childNode))
        .filter(isElementBuilderChild);
}
export function isElementBuilderChild(value) {
    return (isElementDeclaration(value) ||
        isElementBuilder(value) ||
        isViewBuilder(value) ||
        isElementBuilderChildBasic(value));
}
export function isElementBuilderChildren(value) {
    if (!Array.isArray(value))
        return false;
    for (const child of value) {
        if (!isElementBuilderChild(child)) {
            return false;
        }
    }
    return true;
}
export function isSingularElement(value) {
    return isHTMLElement(value) || isSVGElement(value);
}
export function getSingularElementTagName(el) {
    return el.tagName.toLowerCase();
}
export function normalizeChildren(child) {
    if (Array.isArray(child)) {
        return child;
    }
    return [child];
}
/**
 * Determines if a value is a listener declaration.
 *
 * @internal scope: workspace
 */
export function isListenerDeclaration(value) {
    return (typeof value === "object" &&
        value !== null &&
        typeof value.type === "string" &&
        typeof value.handler === "function" &&
        (typeof value.options === "object" ||
            value.options === undefined));
}
export function isElementDeclarationListeners(value) {
    return (typeof value === "object" &&
        value !== null &&
        Object.keys(value).every((key) => isListenerDeclaration(value[key])));
}
export function discernDeclarationListeners(value) {
    return "type" in value;
}
/**
 * The suffix to append to the listener key when it's a capture listener.
 */
const CAPTURE_SUFFIX = "__capture";
/**
 * Create a key for the element declaration listeners object.
 *
 * @remarks
 *
 * The key is used to store the listener declaration in the element declaration.
 *
 * The capture suffix is added when the listener is a capture listener.
 * Adding the capture suffix is necessary to differentiate between the capture and bubble phases,
 * because `removeEventListener` looks for the event type and capture option,
 * when removing a listener.
 */
export function createListenerDeclarationKey(declaration) {
    const { type, options } = declaration;
    if (options?.capture) {
        return `${type}${CAPTURE_SUFFIX}`;
    }
    return `${type}`;
}
